import React, { useCallback, useRef } from "react";
import { useAuth } from "@/context/auth-context";
import { useNotificationSignalR } from "@/hooks/useNotificationSignalR";
import { useNotificationToast } from "@/components/ui/NotificationToastContainer";
import logger from "@/utils/logger";

/**
 * SignalRToastListener
 *
 * This component listens to SignalR notifications and shows toast notifications
 * for trip issues (blocked, delayed, cancelled).
 *
 * It's separate from NotificationPopover to ensure toasts always work
 * regardless of component mounting order.
 */
export const SignalRToastListener: React.FC = () => {
    const { userProfile } = useAuth();
    const { showNotificationToast } = useNotificationToast();

    logger.info("🔔 [SignalRToastListener] Component mounted/rendered");
    logger.info("🔔 [SignalRToastListener] User email:", userProfile?.email);

    // Track last shown toast to prevent duplicates
    const lastToastIdRef = useRef<Record<string, number>>({});

    // SignalR notification handler - ONLY for showing toasts
    const handleNotification = useCallback(
        (notification: unknown) => {
            logger.info(
                "🔔🔔🔔 [SignalRToastListener] ===== HANDLER CALLED ====="
            );

            if (!notification || typeof notification !== "object") {
                logger.warn(
                    "⚠️ [SignalRToastListener] Invalid notification:",
                    notification
                );
                return;
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const customData = (notification as any).customData;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const messageContent = (notification as any).messageContent;

            logger.info(
                "🔔 [SignalRToastListener] Notification received:",
                notification
            );
            logger.info("🔔 [SignalRToastListener] CustomData:", customData);
            logger.info(
                "🔔 [SignalRToastListener] notificationType:",
                customData?.notificationType
            );

            // Feature 27406: Trip deletion is handled by DeliveryIssueToastProvider (large banner)
            // Skip it here to avoid duplicate notifications
            if (
                customData &&
                customData.notificationType === "trip_admin_delete"
            ) {
                logger.info(
                    "⏭️ [SignalRToastListener] Skipping trip_admin_delete - handled by DeliveryIssueToastProvider"
                );
                return; // Early return
            }

            // Check if this is a trip issue notification (blocked, delayed, cancelled)
            if (
                customData &&
                customData.notificationType === "delivery_blocked"
            ) {
                const tripId = customData.tripId
                    ? parseInt(customData.tripId)
                    : undefined;
                const status = customData.status || "blocked";

                // Skip blocked/delayed - DeliveryIssueToastProvider will handle those with banner
                // Only handle "cancelled" status here
                if (status === "blocked" || status === "delayed") {
                    logger.info(
                        "⏭️ [SignalRToastListener] Skipping blocked/delayed (handled by banner):",
                        { tripId, status }
                    );
                    return;
                }

                const statusLabel =
                    status.charAt(0).toUpperCase() + status.slice(1);

                // Create deduplication key (without timestamp)
                const dedupeKey = `trip-${tripId}-${status}`;

                // Only show toast if we haven't shown this exact notification recently (within 10 seconds)
                const now = Date.now();
                const lastShownTime = lastToastIdRef.current[dedupeKey] || 0;

                if (now - lastShownTime > 10000) {
                    // 10 second cooldown per trip+status combo
                    // Update last shown time
                    lastToastIdRef.current[dedupeKey] = now;

                    logger.info(
                        "✅ [SignalRToastListener] Showing toast for trip issue:",
                        { tripId, status, message: messageContent }
                    );

                    // Show the toast notification (no bell sound for cancelled)
                    showNotificationToast({
                        title: `Trip ${tripId ? `#${tripId}` : ""} ${statusLabel}`,
                        message: messageContent || `Trip has been ${status}`,
                        variant: status === "cancelled" ? "danger" : "warning",
                        tripId,
                    });
                } else {
                    logger.info(
                        "⏭️ [SignalRToastListener] Skipping duplicate toast:",
                        {
                            tripId,
                            status,
                            cooldown: 10000 - (now - lastShownTime),
                        }
                    );
                }
            } else {
                logger.info(
                    "⏭️ [SignalRToastListener] Not a known notification type, skipping toast"
                );
            }
        },
        [showNotificationToast]
    );

    // Connect to SignalR for toast notifications only
    logger.info(
        "🔔 [SignalRToastListener] Calling useNotificationSignalR with email:",
        userProfile?.email
    );
    useNotificationSignalR(
        "Dispatch IQ",
        userProfile?.email || "",
        handleNotification,
        undefined, // No trip status change handler needed
        "SignalRToastListener" // Named handler to prevent duplicates
    );

    logger.info(
        "🔔 [SignalRToastListener] SignalR hook setup complete, handler should be registered"
    );

    // This component doesn't render anything
    return null;
};
